import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CircleHelp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { createGuideCatalog, findPageGuide } from './guide-catalog'
import { TourOverlay } from './tour-overlay'
import { readTourProgress, saveTourProgress, type TourProgress } from './tour-progress'
import type { GuideAccess, GuideDefinition } from './guide-types'

export function GuideCenter({ userId, access }: { userId: string; access: GuideAccess }) {
  const location = useLocation()
  const navigate = useNavigate()
  const catalog = useMemo(() => createGuideCatalog(access), [access])
  const current = findPageGuide(catalog, location.pathname)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<GuideDefinition | null>(null)
  const [startStepId, setStartStepId] = useState('overview')

  function startGuide(guide: GuideDefinition, resume = false) {
    const progress = readTourProgress(userId, guide.id)
    setStartStepId(resume && progress?.status === 'in_progress' ? progress.stepId : guide.steps[0]?.id ?? 'overview')
    setActive(guide)
    setOpen(false)
  }

  function finish(status: TourProgress['status'], stepId: string) {
    if (active) saveTourProgress(userId, active.id, { status, stepId })
    setActive(null)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-tour="workhub-guides" type="button" size="sm" variant="outline">
            <CircleHelp className="size-4" />
            Guides
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>WorkHub guides</DialogTitle>
            <DialogDescription>
              These walkthroughs only show sections and actions available to your account.
            </DialogDescription>
          </DialogHeader>

          {current ? (
            <div className="rounded-xl border bg-primary/[0.04] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-primary">On this page</p>
              <h3 className="mt-2 font-bold">{current.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{current.description}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => startGuide(current, false)}>Start guide</Button>
                {readTourProgress(userId, current.id)?.status === 'in_progress' ? (
                  <Button size="sm" variant="outline" onClick={() => startGuide(current, true)}>Resume</Button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <h3 className="text-sm font-bold">Your sections</h3>
            {catalog.map((guide) => (
              <button
                key={guide.id}
                type="button"
                className="w-full rounded-xl border p-3 text-left transition hover:border-primary/30 hover:bg-primary/[0.02]"
                onClick={() => {
                  if (guide.path === location.pathname) startGuide(guide)
                  else {
                    setOpen(false)
                    navigate(guide.path)
                  }
                }}
              >
                <span className="block text-sm font-semibold">{guide.title}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{guide.description}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {active ? (
        <TourOverlay
          title={active.title}
          steps={active.steps}
          startStepId={startStepId}
          onStep={(stepId) => saveTourProgress(userId, active.id, { status: 'in_progress', stepId })}
          onEnd={finish}
        />
      ) : null}
    </>
  )
}
